import React, { useEffect, useMemo, useRef, useState } from "react";
import { Bell, Sun, Moon, Settings, X, Circle, Check, CalendarDays } from "lucide-react";
import logo from "../logo.jpg";
import "./Navbar.css";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import { feedbackApi, profileApi, submissionsApi, tracksApi } from "../../api/client";
import { getApiErrorMessage } from "../../api/http";
import { getActiveTrackId, setActiveTrackId } from "../../utils/session";
import { isTrackOwnedByUser } from "../../utils/track";

const READ_STORAGE_KEY = "ieee_read_notification_ids";

const formatRelativeTime = (value) => {
  if (!value) return "Just now";
  const diff = Date.now() - new Date(value).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hour ago`;
  const days = Math.floor(hours / 24);
  return `${days} day ago`;
};

const getStoredReadIds = () => {
  try {
    const raw = sessionStorage.getItem(READ_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const saveStoredReadIds = (ids) => {
  try {
    sessionStorage.setItem(READ_STORAGE_KEY, JSON.stringify(ids));
  } catch {
    // ignore
  }
};

const buildAnnouncementNotification = (announcement, trackName, trackId) => ({
  id: `announcement-${announcement._id}`,
  type: "announcement",
  title: announcement.title || "Announcement",
  subtitle: trackName || "Track announcement",
  detail: announcement.content || "No details provided.",
  createdAt: announcement.createdAt,
  actionLabel: "Open Announcement",
  actionTo: "/announcements",
  actionQuery: `?trackId=${trackId}`,
  payload: announcement,
  trackId,
});

const buildEnrollmentNotification = (request, trackId) => ({
  id: `enrollment-${request._id}`,
  type: "enrollment",
  title: `${request?.student?.fullName || "Student"} requested enrollment`,
  subtitle: request?.student?.email || "Pending enrollment",
  detail: "Review and approve/reject this enrollment request.",
  createdAt: request.createdAt,
  actionLabel: "Review Request",
  actionTo: "/enrollment-requests",
  actionQuery: trackId ? `?trackId=${trackId}` : "",
  payload: request,
});

const buildSubmissionNotification = (submission, taskId) => ({
  id: `submission-${submission._id}`,
  type: "submission",
  title: `${submission?.student?.fullName || "Student"} submitted task`,
  subtitle: submission?.student?.email || "New submission",
  detail: submission?.status === "graded" ? "Submission graded" : "New assignment submission",
  createdAt: submission?.submittedAt || submission?.createdAt,
  actionLabel: "View Submission",
  actionTo: "/grading-interface",
  actionQuery: taskId ? `?taskId=${taskId}` : "",
  payload: submission,
});

const sortByDateDesc = (rows) =>
  rows.sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime());

const NotificationCard = ({ item, isUnread, onOpen, onAction }) => {
  return (
    <article className={`notification-card ${isUnread ? "unread" : ""}`} onClick={() => onOpen(item)}>
      <div className="notification-card-top">
        <div>
          <h4>{item.title}</h4>
          <p className="notification-subtitle">{item.subtitle}</p>
        </div>
        {isUnread ? <Circle size={10} className="notification-dot" /> : null}
      </div>
      <p className="notification-detail">{item.detail}</p>
      <div className="notification-card-bottom">
        {item.actionLabel ? (
          <button
            type="button"
            className="notification-action-btn"
            onClick={(event) => {
              event.stopPropagation();
              onAction(item);
            }}
          >
            {item.actionLabel}
          </button>
        ) : null}
        <span className="notification-time">{formatRelativeTime(item.createdAt)}</span>
      </div>
    </article>
  );
};

const Navbar = () => {
  const { user, isAuthenticated } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const role = user?.role || "student";
  const userId = user?.id || user?._id;
  const bellButtonRef = useRef(null);
  const drawerRef = useRef(null);

  const [panelOpen, setPanelOpen] = useState(false);
  const [filter, setFilter] = useState("all");
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [notificationError, setNotificationError] = useState("");
  const [notifications, setNotifications] = useState([]);
  const [readIds, setReadIds] = useState(() => new Set(getStoredReadIds()));
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);

  useEffect(() => {
    saveStoredReadIds(Array.from(readIds));
  }, [readIds]);

  useEffect(() => {
    const onClickOutside = (event) => {
      if (!panelOpen) return;
      const target = event.target;
      if (drawerRef.current?.contains(target)) return;
      if (bellButtonRef.current?.contains(target)) return;
      setPanelOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [panelOpen]);

  const markAsRead = (notificationId) => {
    setReadIds((prev) => {
      const next = new Set(prev);
      next.add(notificationId);
      return next;
    });
  };

  const markAllAsRead = () => {
    setReadIds((prev) => {
      const next = new Set(prev);
      notifications.forEach((item) => next.add(item.id));
      return next;
    });
  };

  const loadNotifications = async () => {
    setLoadingNotifications(true);
    setNotificationError("");
    try {
      const next = [];

      if (role === "student") {
        const profileRes = await profileApi.me();
        const tracks = profileRes.data?.profile?.tracks || [];
        if (tracks.length) {
          const selectedTrackId = getActiveTrackId() || tracks[0]._id;
          const selectedTrack = tracks.find((track) => `${track._id}` === `${selectedTrackId}`) || tracks[0];
          if (selectedTrack?._id) setActiveTrackId(selectedTrack._id);
          const { data } = await tracksApi.announcements(selectedTrack?._id);
          const announcements = data?.announcements || [];
          announcements.slice(0, 12).forEach((announcement) => {
            next.push(buildAnnouncementNotification(announcement, selectedTrack?.name, selectedTrack?._id));
          });
        }
      } else {
        const tracksRes = await tracksApi.list();
        const allTracks = tracksRes.data?.tracks || [];
        const ownedTracks =
          role === "instructor"
            ? allTracks.filter((track) => isTrackOwnedByUser(track, userId))
            : allTracks;

        const selectedTrackId = getActiveTrackId();
        const selectedTrack =
          ownedTracks.find((track) => `${track._id}` === `${selectedTrackId}`) ||
          ownedTracks[0] ||
          null;

        if (selectedTrack?._id) {
          setActiveTrackId(selectedTrack._id);

          const [requestsRes, announcementsRes, tasksRes] = await Promise.all([
            tracksApi.requests(selectedTrack._id).catch(() => ({ data: { requests: [] } })),
            tracksApi.announcements(selectedTrack._id).catch(() => ({ data: { announcements: [] } })),
            tracksApi.tasks(selectedTrack._id).catch(() => ({ data: { tasks: [] } })),
          ]);

          const requests = requestsRes.data?.requests || [];
          const announcements = announcementsRes.data?.announcements || [];
          const tasks = tasksRes.data?.tasks || [];

          requests.slice(0, 8).forEach((request) => {
            next.push(buildEnrollmentNotification(request, selectedTrack._id));
          });

          announcements.slice(0, 8).forEach((announcement) => {
            next.push(buildAnnouncementNotification(announcement, selectedTrack.name, selectedTrack._id));
          });

          const taskSlices = tasks.slice(0, 3);
          const submissionResults = await Promise.all(
            taskSlices.map((task) =>
              submissionsApi
                .instructorTaskSubmissions(task._id)
                .then((res) => ({ taskId: task._id, submissions: res.data?.submissions || [] }))
                .catch(() => ({ taskId: task._id, submissions: [] }))
            )
          );

          submissionResults.forEach((entry) => {
            entry.submissions.slice(0, 4).forEach((submission) => {
              next.push(buildSubmissionNotification(submission, entry.taskId));
            });
          });
        }

        if (role !== "student") {
          const feedbackRes = await feedbackApi.list().catch(() => ({ data: { feedback: [] } }));
          const feedbackRows = feedbackRes.data?.feedback || [];
          feedbackRows.slice(0, 4).forEach((item) => {
            next.push({
              id: `feedback-${item._id}`,
              type: "feedback",
              title: `Feedback: ${item.category}`,
              subtitle: item?.user?.fullName || "Learner feedback",
              detail: item?.message || "No message",
              createdAt: item?.createdAt,
              actionLabel: "Open Center",
              actionTo: "/notification-center",
              actionQuery: "",
              payload: item,
            });
          });
        }
      }

      setNotifications(sortByDateDesc(next).slice(0, 30));
    } catch (err) {
      setNotificationError(getApiErrorMessage(err, "Failed to load notifications"));
      setNotifications([]);
    } finally {
      setLoadingNotifications(false);
    }
  };

  useEffect(() => {
    if (!panelOpen) return;
    loadNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panelOpen, role, userId]);

  const linksByRole = {
    student: [
      { to: "/tracks", label: "Tracks" },
      { to: "/student-guide", label: "Guide" },
      { to: "/feedback", label: "Feedback" },
    ],
    instructor: [
      { to: "/tracks", label: "Tracks" },
      { to: "/dashboard", label: "Dashboard" },
      { to: "/notification-center", label: "Notifications" },
      { to: "/create-announcement", label: "Create Announcement" },
      { to: "/create-task", label: "Create Task" },
      { to: "/track-social-preview", label: "Social Share" },
      { to: "/tasks-submitted", label: "Submissions" },
      { to: "/grading-interface", label: "Grading" },
    ],
    super_admin: [
      { to: "/tracks", label: "Tracks" },
      { to: "/dashboard", label: "Dashboard" },
      { to: "/enrollment-requests", label: "Enrollments" },
      { to: "/create-announcement", label: "Create Announcement" },
      { to: "/create-task", label: "Create Task" },
      { to: "/grading-interface", label: "Grading" },
      { to: "/analytics-dashboard", label: "Track Analytics" },
      { to: "/notification-center", label: "Notifications" },
      { to: "/global-leaderboard", label: "Leaderboard" },
      { to: "/super-admin", label: "Control Panel" },
    ],
  };

  const navLinks = linksByRole[role] || linksByRole.student;
  const notificationTarget = role === "student" ? "/announcements" : "/notification-center";

  const unreadCount = useMemo(
    () => notifications.filter((item) => !readIds.has(item.id)).length,
    [notifications, readIds]
  );

  const filteredNotifications = useMemo(() => {
    if (filter === "unread") return notifications.filter((item) => !readIds.has(item.id));
    if (filter === "enrollments") return notifications.filter((item) => item.type === "enrollment");
    return notifications;
  }, [filter, notifications, readIds]);

  const openNotification = (item) => {
    markAsRead(item.id);
    if (item.type === "announcement") {
      setSelectedAnnouncement(item);
      return;
    }
    if (item.actionTo) {
      setPanelOpen(false);
      navigate(`${item.actionTo}${item.actionQuery || ""}`);
    }
  };

  const onNotificationAction = (item) => {
    markAsRead(item.id);
    if (item.type === "announcement") {
      setSelectedAnnouncement(item);
      return;
    }
    if (item.actionTo) {
      setPanelOpen(false);
      navigate(`${item.actionTo}${item.actionQuery || ""}`);
    }
  };

  // Helper function to keep the JSX clean
  const getLinkClass = ({ isActive }) =>
    isActive ? "nav-link active-custom" : "nav-link text-muted-custom";

  if (!isAuthenticated) {
    return null;
  }

  return (
    <>
      <nav className="navbar navbar-expand-lg navbar-light bg-white border-bottom sticky-top py-2">
        <div className="container-fluid px-md-5">
          <NavLink className="navbar-brand d-flex align-items-center fw-bold text-dark" to="/">
            <img
              src={logo}
              className="rounded-circle me-3 shadow-sm"
              alt="IEEE Logo"
              width="42"
              height="42"
              style={{ objectFit: "cover" }}
            />
            <span className="brand-text">IEEE Study Groups</span>
          </NavLink>

          <button className="navbar-toggler border-0" type="button" data-bs-toggle="collapse" data-bs-target="#navbarNav">
            <span className="navbar-toggler-icon"></span>
          </button>

          <div className="collapse navbar-collapse justify-content-end" id="navbarNav">
            <ul className="navbar-nav gap-lg-4 me-3">
              {navLinks.map((item) => (
                <li key={item.to} className="nav-item">
                  <NavLink className={getLinkClass} to={item.to}>{item.label}</NavLink>
                </li>
              ))}
            </ul>
          </div>

          <div className="d-flex align-items-center gap-2 navbar-utility">
            <button
              ref={bellButtonRef}
              className="btn btn-utility rounded-circle bg-body-tertiary notification-bell-btn"
              onClick={() => setPanelOpen((prev) => !prev)}
              title="Notifications"
            >
              <Bell size={20} />
              {unreadCount > 0 ? <span className="notification-counter">{unreadCount > 99 ? "99+" : unreadCount}</span> : null}
            </button>
            <button
              className="btn btn-utility rounded-circle bg-body-tertiary"
              onClick={toggleTheme}
              title={isDark ? "Switch to light mode" : "Switch to dark mode"}
            >
              {isDark ? <Sun size={20} /> : <Moon size={20} />}
            </button>
            <Link to="/profile" className="profile-wrapper ms-2">
              <div className="profile-circle shadow-sm"></div>
            </Link>
          </div>
        </div>
      </nav>

      {panelOpen ? (
        <aside ref={drawerRef} className="notification-drawer">
          <header className="notification-drawer-header">
            <div>
              <h3>Notifications</h3>
              <p>You have {unreadCount} unread message(s)</p>
            </div>
            <div className="notification-header-actions">
              <button type="button" className="notification-icon-btn" onClick={() => navigate(notificationTarget)}>
                <Settings size={14} />
              </button>
              <button type="button" className="notification-icon-btn" onClick={() => setPanelOpen(false)}>
                <X size={14} />
              </button>
            </div>
          </header>

          <div className="notification-tab-row">
            <button type="button" className={`notification-tab ${filter === "all" ? "active" : ""}`} onClick={() => setFilter("all")}>All</button>
            <button type="button" className={`notification-tab ${filter === "unread" ? "active" : ""}`} onClick={() => setFilter("unread")}>Unread</button>
            <button type="button" className={`notification-tab ${filter === "enrollments" ? "active" : ""}`} onClick={() => setFilter("enrollments")}>Enrollments</button>
            <button type="button" className="notification-mark-btn" onClick={markAllAsRead}>
              <Check size={12} />
              Mark all as read
            </button>
          </div>

          <div className="notification-list">
            {loadingNotifications ? <div className="alert alert-light border">Loading notifications...</div> : null}
            {notificationError ? <div className="alert alert-danger py-2">{notificationError}</div> : null}
            {!loadingNotifications && !notificationError && !filteredNotifications.length ? (
              <div className="notification-empty">
                <CalendarDays size={16} />
                No notifications right now.
              </div>
            ) : null}
            {filteredNotifications.map((item) => (
              <NotificationCard
                key={item.id}
                item={item}
                isUnread={!readIds.has(item.id)}
                onOpen={openNotification}
                onAction={onNotificationAction}
              />
            ))}
          </div>

          <footer className="notification-drawer-footer">
            <button type="button" className="notification-history-btn" onClick={() => { setPanelOpen(false); navigate(notificationTarget); }}>
              View Notification History
            </button>
          </footer>
        </aside>
      ) : null}

      {selectedAnnouncement ? (
        <div className="announcement-modal-backdrop" onClick={() => setSelectedAnnouncement(null)}>
          <div className="announcement-modal-card" onClick={(event) => event.stopPropagation()}>
            <header className="announcement-modal-head">
              <h4>{selectedAnnouncement.title}</h4>
              <button type="button" className="notification-icon-btn" onClick={() => setSelectedAnnouncement(null)}>
                <X size={14} />
              </button>
            </header>
            <p className="announcement-modal-subtitle">{selectedAnnouncement.subtitle}</p>
            <p className="announcement-modal-time">{formatRelativeTime(selectedAnnouncement.createdAt)}</p>
            <p className="announcement-modal-body">{selectedAnnouncement.detail}</p>
            <div className="announcement-modal-actions">
              <button
                type="button"
                className="notification-action-btn primary"
                onClick={() => {
                  setSelectedAnnouncement(null);
                  setPanelOpen(false);
                  navigate(`${selectedAnnouncement.actionTo}${selectedAnnouncement.actionQuery || ""}`);
                }}
              >
                Open Full Announcement
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default Navbar;
